import React from 'react';
import SelectFieldCheckbox from './SelectFieldCheckbox';
import stringHelper from '../../../../Helpers/stringHelper';
import TpEvaluatorService from '../../../../Services/TpEvaluatorService';
import ErrorMessage from '../../../ErrorMessage';

// Search-as-you-type evaluator picker: reuses the plain Select (SelectFieldCheckbox / SelectWithFilter) for both
// the dropdown and its built-in filter box, but drives the options from a backend "email contains" search
// instead of a fixed list. Only searches once at least MIN_SEARCH_LENGTH characters are typed, debounced so it
// doesn't fire on every keystroke. The value it stores is the TpEvaluatorId (bound to the freelancer's
// PitpEvaluatorId field).
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DELAY_MS = 1500;
const EMPTY_OPTION = { label: 'Choose one...', value: '', disabled: false };

export default class TpEvaluatorSelect extends React.Component {
  static defaultProps = {
    value: null,
    className: 'w-100 form-control form-control-sm',
    filterCleared: false,
    fieldName: 'defaultFieldname',
    onChange: () => {},
    editedItem: {},
  };

  constructor(props) {
    super(props);
    // Seed the currently linked evaluator (populated when editing) so it shows without a backend call, using
    // the same "Name (Id)" text the old read-only field displayed.
    const selectedOption = TpEvaluatorSelect.buildSelectedOption(props);
    this.state = {
      selectValues: selectedOption ? [EMPTY_OPTION, selectedOption] : [EMPTY_OPTION],
      selectedOption,
    };
    this.searchTimer = null;
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
  }

  static buildSelectedOption(props) {
    if (!props.value) {
      return null;
    }
    const email = props.editedItem?.EvaluatorEmail;
    const label = email ? `${email}${stringHelper.toBracket(props.value)}` : `${props.value}`;
    return { label, value: props.value, disabled: false };
  }

  handleFilterChange(search) {
    clearTimeout(this.searchTimer);

    if (search.trim().length < MIN_SEARCH_LENGTH) {
      this.setState({ selectValues: this.withSelectedOption([EMPTY_OPTION]) });
      return;
    }

    this.searchTimer = setTimeout(() => this.searchEvaluators(search.trim()), SEARCH_DELAY_MS);
  }

  searchEvaluators(search) {
    TpEvaluatorService.SearchEvaluatorsByEmail(search)
      .then((results) => {
        const options = [EMPTY_OPTION];
        (results ?? []).forEach((evaluator) => {
          options.push({
            label: `${evaluator.email}${stringHelper.toBracket(evaluator.tpEvaluatorId)}`,
            value: evaluator.tpEvaluatorId,
            disabled: false,
          });
        });
        this.setState({ selectValues: this.withSelectedOption(options) });
      })
      .catch(() => {
        this.setState({ selectValues: this.withSelectedOption([EMPTY_OPTION]) });
        ErrorMessage.SetErrorMessage('TPES68');
      });
  }

  // Keep the currently selected evaluator selectable even when a new search wouldn't return it.
  withSelectedOption(options) {
    const selected = this.state.selectedOption;
    if (selected && !options.some((o) => `${o.value}` === `${selected.value}`)) {
      return [...options, selected];
    }
    return options;
  }

  handleSelectChange(fieldName, value) {
    const selectedOption = this.state.selectValues.find((o) => `${o.value}` === `${value}` && o.value !== '');
    this.setState({ selectedOption: selectedOption ?? null });
    this.props.onChange(fieldName, value);
  }

  render() {
    return (
      <SelectFieldCheckbox
        value={this.props.value}
        className={this.props.className}
        fieldName={this.props.fieldName}
        onChange={this.handleSelectChange}
        filterCleared={this.props.filterCleared}
        selectValues={this.state.selectValues}
        onFilterChange={this.handleFilterChange}
        filterPlaceholder={`Type at least ${MIN_SEARCH_LENGTH} characters of the evaluator email...`}
      />
    );
  }
}
